/* eslint-disable import/no-unused-modules */
import { MaxUint256 } from '@ethersproject/constants'
import { useWeb3React } from '@web3-react/core'
import BN from 'bignumber.js'
import { useMasterchefV2, useTokenContract } from 'hooks/useContract'
import { useCallback, useEffect, useState } from 'react'

import { masterChefV2, mjtAddress } from './constants'

const pow18 = new BN(10).pow(18)
const KCC_BLOCK_TIME = 2.2987
const BLOCKS_PER_YEAR = new BN((60 / KCC_BLOCK_TIME) * 60 * 24 * 365) // 10512000

function calculateMjtPerBlockV2ByTimestamp() {
  const MJT_V2_PER_BLOCK_INIT = new BN(3)
  const startTimestamp = new Date('2021-11-21 12:57:01').getTime()
  const nowTimestamp = new Date().getTime()
  const allTimestamp = new BN(nowTimestamp).minus(startTimestamp).toNumber()
  const days180 = 60 * 60 * 24 * 1000 * 180

  if (days180 > allTimestamp) {
    return MJT_V2_PER_BLOCK_INIT
  }
  const n = Number(new BN(allTimestamp).div(days180).toFixed(0, 1))
  let kccPerBlock = 3
  for (let i = 0; i < n; i++) {
    kccPerBlock -= kccPerBlock * 0.2
  }
  return new BN(kccPerBlock)
}

function calculateMjtV2MigrateBlock(version: number) {
  const theoryBlock = calculateMjtPerBlockV2ByTimestamp()
  console.log('calculateMjtV2MigrateBlock', theoryBlock.toString())
  return version > 1 ? theoryBlock : new BN(0)
}

const MJT_PER_BLOCK_V2 = calculateMjtV2MigrateBlock(2).multipliedBy(0.85).multipliedBy(0.2)

export const usePoolTotalStaked = () => {
  const mjtContract = useTokenContract(mjtAddress, true)
  const [totalStake, setTotalStake] = useState<BN>(new BN(0))

  const updatePoolTotalStaked = useCallback(async () => {
    try {
      const result = await mjtContract?.balanceOf(masterChefV2)
      setTotalStake(new BN(result?.toString() as any).div(pow18))
    } catch (e) {
      console.log('e =', e)
    }
  }, [mjtContract])

  useEffect(() => {
    updatePoolTotalStaked()
    const timer = setInterval(() => {
      updatePoolTotalStaked()
    }, 10000)
    return () => clearInterval(timer)
  }, [updatePoolTotalStaked])

  return { totalStake, updatePoolTotalStaked }
}

/**
 * Get the APR value in %
 * @param stakingTokenPrice Token price in the same quote currency
 * @param rewardTokenPrice Token price in the same quote currency
 * @param totalStaked Total amount of stakingToken in the pool
 * @param tokenPerBlock Amount of new cake allocated to the pool for each new block
 * @returns Null if the APR is NaN or infinite.
 */
export const getPoolApr = (totalStaked: BN): any => {
  const tokenPerBlock = String(MJT_PER_BLOCK_V2.multipliedBy(0.25))
  const totalRewardPricePerYear = new BN(tokenPerBlock).times(BLOCKS_PER_YEAR)
  const totalStakingTokenInPool = new BN(totalStaked)
  const apr = totalRewardPricePerYear.div(totalStakingTokenInPool).times(100)
  return apr.isNaN() || !apr.isFinite() ? null : apr.toNumber()
}

export const useAllowanceAndApprove = () => {
  const [allowanceAmount, setAllowanceAmount] = useState<BN>(new BN(0))
  const [isApproving, setIsApproving] = useState(false)
  const masterChefV2Contract = useMasterchefV2()
  const mjtContract = useTokenContract(mjtAddress, true)

  const { account } = useWeb3React()

  const updateAllowanceAmount = useCallback(async () => {
    try {
      const allowanceResult = await mjtContract?.allowance(account as string, masterChefV2)
      // console.log('allowanceResult =', allowanceResult)
      setAllowanceAmount(new BN(allowanceResult?.toString() as string).div(pow18))
    } catch (e) {
      console.debug('allowance err', e)
    }
  }, [account, mjtContract])

  const approveToken = async () => {
    setIsApproving(true)
    try {
      const approveResult = await mjtContract?.approve(masterChefV2, MaxUint256)
      const txReceipt = await approveResult?.wait()
      if (txReceipt && txReceipt?.status) {
        updateAllowanceAmount()
      }
    } finally {
      setIsApproving(false)
    }
  }

  const checkApprove = async (amount?: string) => {
    if (!account || !masterChefV2Contract) return
    const approveAmount = amount ? new BN(amount) : MaxUint256
    await updateAllowanceAmount()
    // console.log('approveAmount =', allowanceAmount.toString(), 'approveAmount =', approveAmount.toString())
    if (allowanceAmount.isLessThan(approveAmount.toString())) {
      await approveToken()
    }
  }

  useEffect(() => {
    if (account) {
      updateAllowanceAmount()
    }
  }, [account, updateAllowanceAmount])

  return { checkApprove, allowanceAmount, isApproving }
}

export const useMjtBalance = () => {
  const [mjtBalance, setMjtBalance] = useState<BN>(new BN(0))
  const mjtContract = useTokenContract(mjtAddress, true)
  const { account } = useWeb3React()

  const updateMjtBalance = useCallback(async () => {
    if (!account || !mjtContract) return
    const mjtResult = await mjtContract?.balanceOf(account as string)
    setMjtBalance(new BN(mjtResult?.toString() as string).div(pow18))
  }, [account, mjtContract])

  useEffect(() => {
    let timer: any = null
    if (account) {
      updateMjtBalance()
      timer = setInterval(() => {
        updateMjtBalance()
      }, 10000)
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [account, updateMjtBalance])

  return { mjtBalance, updateMjtBalance }
}

export const useStakeBalance = () => {
  const [stakeBalance, setStakeBalance] = useState<BN>(new BN(0))
  const masterChefV2Contract = useMasterchefV2()
  const { account } = useWeb3React()
  const updateStakeBalance = useCallback(async () => {
    try {
      const stakeResult = await masterChefV2Contract?.userInfo(0, account)
      setStakeBalance(new BN(stakeResult.amount.toString(10)).div(pow18))
    } catch (e) {
      console.log('stake balance error =', e)
    }
  }, [masterChefV2Contract, account])

  useEffect(() => {
    let timer: any = null
    if (account) {
      updateStakeBalance()
      timer = setInterval(() => {
        updateStakeBalance()
      }, 10000)
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [account, updateStakeBalance])

  return { stakeBalance, updateStakeBalance }
}

export const usePendingReward = () => {
  const [pendingReward, setPendingReward] = useState<BN>(new BN(0))
  const masterChefV2Contract = useMasterchefV2()
  const { account } = useWeb3React()

  const updatePendingReward = useCallback(async () => {
    try {
      const pendingRewardResult = await masterChefV2Contract?.pendingMojito(0, account)
      // console.log('pendingRewardResult =', new BN(pendingRewardResult.toString()).div(pow18).toString())
      setPendingReward(new BN(pendingRewardResult.toString()).div(pow18))
    } catch (e) {
      console.debug('reward error =', e)
    }
  }, [masterChefV2Contract, account])

  useEffect(() => {
    let timer: any = null
    if (account) {
      updatePendingReward()
      timer = setInterval(() => {
        updatePendingReward()
      }, 10000)
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [account, updatePendingReward])

  return { pendingReward, updatePendingReward }
}

// compound & stake
export const useStake = () => {
  const [isStaking, setIsStaking] = useState(false)
  const masterChefV2Contract = useMasterchefV2()
  const { updatePendingReward } = usePendingReward()
  const { updateStakeBalance } = useStakeBalance()
  const { updatePoolTotalStaked } = usePoolTotalStaked()
  const { updateMjtBalance } = useMjtBalance()
  const { checkApprove } = useAllowanceAndApprove()

  const stake = async (amount: string) => {
    setIsStaking(true)
    try {
      await checkApprove(amount)
      const stakeResult = await masterChefV2Contract?.enterStaking(new BN(amount).times(pow18).toString(10))
      const txReceipt = await stakeResult?.wait()
      if (txReceipt && txReceipt?.status) {
        await updatePendingReward()
        await updateStakeBalance()
        await updatePoolTotalStaked()
        await updateMjtBalance()
      }
    } catch (e) {
      console.log('stake error =', e)
    }
    setIsStaking(false)
  }

  return { stake, isStaking }
}

// harvest
export const useHarvest = () => {
  const [isHarvesting, setIsHarvesting] = useState(false)
  const masterChefV2Contract = useMasterchefV2()
  const { updatePendingReward } = usePendingReward()
  const { updateMjtBalance } = useMjtBalance()

  const harvest = async () => {
    setIsHarvesting(true)
    try {
      const harvestResult = await masterChefV2Contract?.leaveStaking(0)
      const txReceipt = await harvestResult?.wait()
      console.log('harvestResult =', txReceipt)
      if (txReceipt && txReceipt?.status) {
        await updatePendingReward()
        await updateMjtBalance()
      }
    } finally {
      setIsHarvesting(false)
    }
  }

  return { harvest, isHarvesting }
}

export const useUnStake = () => {
  const [isUnStaking, setIsUnStaking] = useState(false)
  const masterChefV2Contract = useMasterchefV2()
  const { updateStakeBalance } = useStakeBalance()
  const { updatePoolTotalStaked } = usePoolTotalStaked()
  const { updateMjtBalance } = useMjtBalance()

  const unStake = async (amount: string) => {
    setIsUnStaking(true)
    try {
      const unStakeResult = await masterChefV2Contract?.leaveStaking(new BN(amount).times(pow18).toString(10))
      const txReceipt = await unStakeResult?.wait()
      if (txReceipt && txReceipt?.status) {
        console.log('unStakeResult =', txReceipt)
        await updateStakeBalance()
        await updatePoolTotalStaked()
        await updateMjtBalance()
      }
    } finally {
      setIsUnStaking(false)
    }
  }

  return { unStake, isUnStaking }
}

export const addTokenToWallet = async (): Promise<boolean> => {
  if (!window.ethereum) return false
  // @ts-ignore
  const tokenAdd = await window.ethereum?.request({
    method: 'wallet_watchAsset',
    params: {
      type: 'ERC20',
      options: {
        address: mjtAddress,
        symbol: 'MJT',
        decimals: 18,
        image: 'https://static-nft.mojitoswap.finance/token/mjt.png',
      },
    },
  })

  return tokenAdd
}
